import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { Menu, X, Sun, Moon } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f38464ee-0c0b-455c-af8c-d4927305d805/artifacts/0w6s5t9g_Quran%20online.png";
const CALENDLY_URL = 'https://calendly.com/ismail-haamud98/30min';

const Navbar = () => {
  const { t, language, toggleLanguage } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/', label: language === 'sv' ? 'Hem' : 'Home' },
    { path: '/programs', label: language === 'sv' ? 'Kurser' : 'Courses' },
    { path: '/tajweed-bok', label: language === 'sv' ? 'Tajweed Guide' : 'Tajweed Guide' },
    { path: '/noorania-bok', label: language === 'sv' ? 'Quran Online Method' : 'Quran Online Method' },
    { path: '/quran', label: language === 'sv' ? 'Koranen' : 'Quran' },
    { path: '/about', label: language === 'sv' ? 'Om oss' : 'About' },
    { path: '/contact', label: language === 'sv' ? 'Kontakt' : 'Contact' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-header" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img src={LOGO_URL} alt="Quran Online Sverige" className="h-12 w-auto" />
            <span className="text-lg font-semibold hidden sm:block tracking-wide">
              <span style={{ color: 'var(--text-primary)' }}>Quran Online </span>
              <span className="text-gold">Sverige</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link key={link.path} to={link.path}
                className={`nav-link ${isActive(link.path) ? 'nav-link-active' : ''}`}
                data-testid={`nav-${link.path.replace('/', '') || 'home'}`}>
                {link.label}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <button onClick={toggleTheme} className="theme-toggle" data-testid="theme-toggle" aria-label="Toggle theme">
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button onClick={toggleLanguage} className="theme-toggle" data-testid="language-toggle">
              <span className="text-xs font-semibold">{language === 'sv' ? 'EN' : 'SV'}</span>
            </button>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-gold ml-2" data-testid="book-link">
              {t('nav.book')}
            </a>
          </div>

          <div className="flex md:hidden items-center gap-2">
            <button onClick={toggleTheme} className="theme-toggle" data-testid="theme-toggle-mobile">
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button className="p-2" style={{ color: 'var(--text-primary)' }}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-6" style={{ borderTop: '1px solid var(--border-color)' }} data-testid="mobile-menu">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link key={link.path} to={link.path}
                  className={`px-4 py-3 text-sm uppercase tracking-wide ${isActive(link.path) ? 'text-gold' : ''}`}
                  style={{ color: isActive(link.path) ? 'var(--gold)' : 'var(--text-secondary)' }}
                  onClick={() => setMobileMenuOpen(false)}>
                  {link.label}
                </Link>
              ))}
              <hr style={{ margin: '1rem 0', borderColor: 'var(--border-color)' }} />
              <button onClick={toggleLanguage} className="px-4 py-3 text-left text-sm uppercase tracking-wide"
                style={{ color: 'var(--text-secondary)' }}>
                {language === 'sv' ? 'English' : 'Svenska'}
              </button>
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="mx-4 mt-2 btn-gold text-center" onClick={() => setMobileMenuOpen(false)}>
                {t('nav.book')}
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
